import { memo, useCallback, useId, type ReactNode } from 'react'

interface DrawerShellProps {
  children: ReactNode
  className?: string
  closeDisabled?: boolean
  dismissOnOverlay?: boolean
  headerActions?: ReactNode
  headerMeta?: ReactNode
  onClose: () => void
  subtitle?: ReactNode
  title: string
  titlePrefix?: ReactNode
  visible?: boolean
}

export const DrawerShell = memo(function DrawerShell({
  children,
  className = '',
  closeDisabled = false,
  dismissOnOverlay = true,
  headerActions,
  headerMeta,
  onClose,
  subtitle,
  title,
  titlePrefix,
  visible = true,
}: DrawerShellProps) {
  const titleId = useId()

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Escape' || closeDisabled) return
    event.preventDefault()
    onClose()
  }, [closeDisabled, onClose])

  const handleOverlayClick = useCallback(() => {
    if (dismissOnOverlay && !closeDisabled) onClose()
  }, [closeDisabled, dismissOnOverlay, onClose])

  return (
    <>
      <div className={`tp-drawer-overlay${visible ? ' is-visible' : ''}`} onMouseDown={handleOverlayClick} aria-hidden="true" />
      <aside
        className={`tp-drawer${visible ? ' is-visible' : ''}${className ? ` ${className}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={handleKeyDown}
      >
        <header className="tp-drawer__header">
          <div className="tp-drawer__heading">
            {headerMeta && <div className="tp-drawer__meta">{headerMeta}</div>}
            <h2 id={titleId} className="tp-drawer__title">
              {titlePrefix}
              {title}
            </h2>
            {subtitle && <div className="tp-drawer__subtitle">{subtitle}</div>}
          </div>
          {headerActions && <div className="tp-drawer__header-actions">{headerActions}</div>}
          <button type="button" className="tp-icon-button tp-drawer__close" onClick={onClose} aria-label="Fermer" disabled={closeDisabled}>×</button>
        </header>
        {children}
      </aside>
    </>
  )
})
